import { c as _c } from "react/compiler-runtime";
import { Button, CloseButton, UseComboboxOptions, Alert, Modal } from '@mantine/core';
import { usePortal } from 'components/portal/portal';
import { SubqItem, subqStatusMd, useSubqQuery } from './subq';
import { SubqCfg, subqCfgForNew, subqTypes, zQuestionForecastType, zSubqCfg, zSubqType } from 'shared/forecast';
import { notNull, Prettify } from 'shared/type';
import { filterObj, groupMap, isNullOrEmpty, notNullOrEmpty } from 'shared/collection';
import { Paths, pathValue } from 'shared/paths';
import { doTo, okOrThrow } from 'shared/promise';
import { HrText, Markdown, MarkdownUtils as Md } from 'shared/ui';
import { useContext, useMemo } from 'react';
import { isArray, isEmpty, isString, keys, pick, sortBy, sum } from 'remeda';
import { match } from 'ts-pattern';
import { useUIItem, useUIState } from 'components/hooks/useUIState';
import { QuestionContext, QuestionContextProps, useQuestionContext } from './questionComponentTypes';
import { QuestionTitle, QuestionTitleForm, useQuestionTitleForm } from './QuestionTitle';
import { useQueryString } from 'utils/useQueryString';
import { zQuestionQuery } from './questionComponentTypes';
import { useDebouncedValue, useFocusTrap } from '@mantine/hooks';
import { z } from 'zod';
import Fuse, { FuseResult } from 'fuse.js';
import { removeStopwords, eng } from 'stopword';
import { KbdCombo, useKbd } from 'components/Keyboard';
import { UseMutationResult } from '@tanstack/react-query';
import { MarkText, markMd } from 'components/form/MarkText';
import rehypeRaw from 'rehype-raw';
import { UseTour, useTourContext, tourTipProps } from 'components/portal/Tour';
import { ForecastTypeCombo, reactionPropsForVote, SubqSectionGroup, SubqTypeCombo, SubqTypeIcon, onSubqTypeChange, SubqGroup } from './Subqs';
import { CardCollapsable } from '../Card';
import { InfoLabel } from 'components/Info';
import { ResolutionsEdit } from './Resolution';
import { AutoConfigureCard, useNewQModal } from './QuestionNew';
import { AutoQ as A } from 'api/autoQ';
import { dbFormSubset } from 'components/hooks/useDbForm';
import { useDebounceMutation } from 'components/hooks/useDebounceMutation';
import { ComparisonSectionControlled, useComparisonNew } from './Comparison';
import { useCtrlEnter } from 'components/Buttons';
import { Sticky } from 'components/Sticky';
import { twMerge } from 'tailwind-merge';
import { Glass } from 'components/Glass';
import { QuestionCommonForm } from './QuestionForm';
import { useQuestionVotesQuery } from './reactions';
const forecastStopwords = ['are', 'average', 'be', 'been', 'being', 'by', 'could', 'did', 'do', 'does', 'had', 'has', 'have', 'having', 'how', 'if', 'is', 'reported', 'resolution', 'resolutions', 'should', 'source', 'that', 'the', 'these', 'this', 'those', 'to', 'was', 'were', 'what', 'when', 'where', 'which', 'who', 'why', 'wil', 'will', 'would', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December', 'Jan', 'Feb', 'Mar', 'Apr', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
type GroupSubqsForNew = Pick<SubqItem, 'id' | 'created_by'> & {
  cfg: Prettify<Pick<SubqItem['cfg'], 'title' | 'description' | 'subTitle' | 'subquestionType' | 'forecastType' | 'status' | 'subquestionOrder'>>;
};
export const useSubqSearch = t0 => {
  const $ = _c(15);
  const {
    value,
    items,
    parentTitle
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = new Fuse(items ?? [], {
      keys: [{
        name: "cfg.title",
        weight: 1
      }, {
        name: "cfg.description",
        weight: 0.3
      }],
      includeMatches: true,
      threshold: 0.2,
      minMatchCharLength: 4,
      ignoreLocation: true,
      useExtendedSearch: true,
      getFn: _temp
    });
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const fuse = t1;
  const processWords = _temp6;
  let t2;
  if ($[2] !== parentTitle) {
    t2 = parentTitle ? new Set(processWords(parentTitle)) : new Set();
    $[2] = parentTitle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const parentWords = t2;
  let t3;
  if ($[4] !== parentWords || $[5] !== value) {
    let t4;
    if ($[7] !== parentWords) {
      t4 = w_3 => !parentWords.has(w_3);
      $[7] = parentWords;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = processWords(value).filter(t4);
    $[4] = parentWords;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const words = t3;
  const [wordsDebounced] = useDebouncedValue(words, 400);
  let t4;
  if ($[9] !== fuse || $[10] !== wordsDebounced) {
    t4 = isEmpty(wordsDebounced) ? [] : sortBy(doTo(fuse.search(wordsDebounced.join(" ")), rs => !isEmpty(rs) ? rs : groupMap(wordsDebounced.flatMap(w_4 => fuse.search(w_4)).filter(_temp7), _temp8, _temp11) as Omit<FuseResult<GroupSubqsForNew>, 'refIndex'>[]), [_temp12, "asc"]);
    $[9] = fuse;
    $[10] = wordsDebounced;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const searchResults = t4;
  let t5;
  if ($[12] !== searchResults || $[13] !== value) {
    t5 = {
      searchResults,
      value
    };
    $[12] = searchResults;
    $[13] = value;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
const ctrlEnter = {
  key: 'Enter',
  mods: ['Meta']
} satisfies KbdCombo;
export type NewSubqCfg = SubqCfg | Pick<SubqCfg, 'title' | 'subTitle' | 'description'>;
export const NewSubqButton = t0 => {
  const $ = _c(22);
  const {
    create,
    group,
    isSticking
  } = t0;
  const tour = useTourContext();
  const {
    isOwner
  } = useQuestionContext();
  const t1 = `new-subq-${group.id}`;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      key: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    modalProps,
    close,
    opened,
    toggle
  } = useNewQModal(t2);
  let t3;
  if ($[2] !== close || $[3] !== create || $[4] !== group || $[5] !== tour) {
    t3 = <NewSubqDropdown group={group} create={create} onClose={close} tour={tour} />;
    $[2] = close;
    $[3] = create;
    $[4] = group;
    $[5] = tour;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== modalProps || $[8] !== t3) {
    t4 = <Modal {...modalProps}>{t3}</Modal>;
    $[7] = modalProps;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = !isSticking;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      content: twMerge("flex gap-2 items-center px-3 py-2 group-data-[sticking=false]:border rounded-lg", "font-semibold cursor-pointer hover:bg-[color-mix(in_oklch,var(--mantine-color-primary-filled)_10%,transparent)]")
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = isOwner && !opened;
  let t8;
  if ($[11] !== t7) {
    t8 = tourTipProps("new-question-propose-subq", t7);
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t10;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex items-center -space-x-1">{subqTypes.map(_temp13)}</div>;
    t10 = <div>Propose a sub-question...</div>;
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  let t11;
  if ($[15] !== t5 || $[16] !== t8 || $[17] !== toggle) {
    t11 = <Glass disableAnimation={true} disabled={t5} frost={0.8} classNames={t6} {...t8} onClick={toggle}>{t9}{t10}</Glass>;
    $[15] = t5;
    $[16] = t8;
    $[17] = toggle;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t4) {
    t12 = <>{t4}{t11}</>;
    $[19] = t11;
    $[20] = t4;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  return t12;
};
const titleProps = ['title', 'subTitle', 'description'] as const;
const subqNewTypeName = 'subq-new';
const zSubqNewForm = z.object({
  userCfg: z.object({
    subquestionType: zSubqType.optional(),
    forecastType: zQuestionForecastType.optional(),
    unitLabel: z.string().optional()
  }).strip(),
  question: zSubqCfg,
  forRequest: A.zReqSubq.shape.priorRequest
}).describe('subq-new');
const initial = zSubqNewForm.parse({
  question: subqCfgForNew,
  userCfg: {},
  forRequest: undefined
});
const NewSubqDropdown = t0 => {
  const $ = _c(42);
  const {
    group,
    create,
    onClose: onCloseRaw,
    tour
  } = t0;
  const {
    profile,
    workspace_id
  } = usePortal();
  const {
    question_id,
    isOwner
  } = useQuestionContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = zQuestionQuery.pick({
      subquestion: true
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const qs = useQueryString(t1);
  let t2;
  if ($[1] !== group.id) {
    t2 = {
      path: [group.id, "subq-new"],
      mode: "user"
    };
    $[1] = group.id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const uiState = useUIState(t2);
  let t3;
  if ($[3] !== uiState) {
    t3 = {
      schema: zSubqNewForm,
      type: subqNewTypeName,
      state: uiState,
      initial
    };
    $[3] = uiState;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const rootForm = useUIItem(t3);
  const userCfgProps = ["subquestionType", "forecastType", "amountType"] as const;
  const userCfgForm = dbFormSubset(rootForm, "userCfg");
  const t4 = group.isRootQuestion ? null : group.id;
  let t5;
  if ($[5] !== question_id || $[6] !== t4) {
    t5 = {
      question_id,
      parent_id: t4,
      pick: _temp14
    };
    $[5] = question_id;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const subqs = useSubqQuery(t5).data;
  const autoReqProps = t6 => {
    const {
      question,
      options
    } = t6;
    return A.zReqSubq.parse({
      type: "subq",
      question: {
        ...(question ? pick(question, [...titleProps, ...userCfgProps]) : {
          ...pick(qForm.values, titleProps),
          ...pick(userCfgForm.values, userCfgProps)
        }),
        parent: pick(group.cfg, ["title", "subTitle", "description", "forecastType"])
      },
      priorRequest: rootForm.values?.forRequest,
      options: {
        workspace_id,
        ...options
      }
    });
  };
  const qForm = dbFormSubset(rootForm, "question", {
    onChange: t7 => {
      const {
        cfg: cfg_0,
        delta,
        description
      } = t7;
      if (description == null || !["user", "accept"].includes(description)) {
        return;
      }
      const deltaProps = keys(delta);
      const userDelta = filterObj(delta, p => isString(p) && userCfgProps.includes(p as any)) as Partial<SubqCfg>;
      if (notNullOrEmpty(userDelta)) {
        userCfgForm.setValues(userDelta);
      }
      if (!deltaProps.some(p_0 => [...titleProps, ...userCfgProps].includes(p_0 as any))) {
        return;
      }
      if (deltaProps.length === 1 && deltaProps[0] === "subquestionType" && delta.subquestionType !== "cause") {
        return;
      }
      const autoQProps = autoReqProps({
        question: {
          ...pick(cfg_0, titleProps),
          ...userDelta
        },
        options: {
          forceRefresh: notNullOrEmpty(userDelta)
        }
      });
      if (isNullOrEmpty(autoQProps.question.title?.trim())) {
        return;
      }
      autoMutation.debouncedMutate(autoQProps);
    },
    defaultDescription: "user"
  });
  const subq = zSubqCfg.parse({
    ...subqCfgForNew,
    ...qForm.values
  });
  const createProposed = withSuggestions => create.mutate(withSuggestions ? subq : zSubqCfg.parse({
    ...subqCfgForNew,
    ...pick(subq, titleProps)
  }), {
    onSuccess: () => onClose({
      clear: true
    })
  });
  const autoMutation = useDebounceMutation({
    mutationFn: _temp15,
    onSuccess: t8 => {
      const {
        forRequest,
        question: question_1,
        usePrior: usePrior_0
      } = t8;
      if (!question_1 || usePrior_0) {
        return;
      }
      rootForm.setValues({
        forRequest,
        ...qForm.deltaWithFullPath(question_1)
      }, "auto");
    }
  });
  const titleForm = useQuestionTitleForm({
    readonly: false,
    qForm,
    typeLabel: "sub-question",
    uiState,
    parent: pick(group.cfg, ["title", "subTitle", "description"])
  });
  const search = useSubqSearch({
    items: subqs,
    value: subq.title,
    label: subq.title,
    parentTitle: group.cfg.title,
    onDropdownOpen() {
      if (tour.tip?.id == "new-question-propose-subq") {
        tour.next("ok");
      }
    }
  });
  const titlePopupFocusRef = useFocusTrap();
  const onClose = options_0 => {
    if (options_0?.clear) {
      rootForm.del();
      titleForm.suggestionsForm.del();
      comparisonProps.clearUnselected();
    }
    onCloseRaw();
  };
  const ctrlEnter = useCtrlEnter({
    onClick: () => createProposed(true)
  });
  const hasAutoQ = !!rootForm.values.forRequest;
  const comparisonProps = useComparisonNew({
    qForm,
    comparisonKey: {
      type: subq.forecastType,
      amountType: subq.amountType ?? "number",
      code: subq.forecastType
    }
  });
  let t9;
  if ($[8] !== subqs) {
    t9 = rs => rs.filter(r => subqs?.some(sq => sq.id == r.subquestion_id));
    $[8] = subqs;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== question_id || $[11] !== t9) {
    t10 = {
      question_id,
      select: t9
    };
    $[10] = question_id;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const uVotes = useQuestionVotesQuery(t10);
  const t11 = ctrlEnter.onKeyDown;
  const t12 = "flex items-center gap-4 mt-2";
  let t13;
  if ($[13] !== isOwner) {
    t13 = tourTipProps("new-question-propose-subq", isOwner);
    $[13] = isOwner;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== create.error || $[16] !== create.isError) {
    t14 = create.isError && <Alert color="red">{create.error.message}</Alert>;
    $[15] = create.error;
    $[16] = create.isError;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const t15 = notNullOrEmpty(subq.title) && <Button className="ml-auto" variant="outline" loading={create.isPending} onClick={() => createProposed(false)}>Propose (text only)</Button>;
  let t16;
  if ($[18] !== t13 || $[19] !== t14 || $[20] !== t15) {
    t16 = <div className={t12} {...t13}>{t14}{t15}</div>;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== profile || $[23] !== qs || $[24] !== search.searchResults || $[25] !== uVotes) {
    let t18;
    if ($[27] !== profile || $[28] !== qs || $[29] !== uVotes) {
      t18 = sr => <div className="grow space-y-4"><CardCollapsable title="Similar" color="purple" classNames={{
          container: "py-2 space-y-2 px-4",
          title: "sticky top-0 z-10 text-sm"
        }}><InfoLabel>{sr.length} similar sub-question already exists. If it looks like a duplicate, open or vote on it</InfoLabel>{sr?.map(t19 => {
            const {
              item: sq_0,
              matches
            } = t19;
            return <div key={sq_0.id} className="data-[combobox-selected=true]:!bg-teal-500/10 data-[combobox-selected=true]:!text-gray-900 border rounded-md space-y-1 p-2 cursor-pointer" onClick={() => qs.setQuery({
              subquestion: sq_0.id
            })}><QuestionTitle title={<MarkText marks={matches?.find(_temp17)?.indices}>{sq_0.cfg.title}</MarkText>} description={<Markdown rehypePlugins={[rehypeRaw]}>{markMd({
                  md: sq_0.cfg.description,
                  marks: matches?.find(_temp18)?.indices
                })}</Markdown>} type="subq" {...pick(sq_0.cfg, ["subquestionType", "forecastType", "status"])} created_by={sq_0.created_by} vote={sq_0.cfg.status == "proposed" ? reactionPropsForVote({
                subq: sq_0,
                vote: uVotes.data?.find(r_0 => r_0.subquestion_id == sq_0.id),
                profile
              }) : undefined} /></div>;
          })}</CardCollapsable></div>;
      $[27] = profile;
      $[28] = qs;
      $[29] = uVotes;
      $[30] = t18;
    } else {
      t18 = $[30];
    }
    t17 = match(search.searchResults).when(isNullOrEmpty, _temp16).otherwise(t18);
    $[22] = profile;
    $[23] = qs;
    $[24] = search.searchResults;
    $[25] = uVotes;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[31] !== autoMutation || $[32] !== autoReqProps || $[33] !== comparisonProps || $[34] !== create.isPending || $[35] !== ctrlEnter.buttonProps || $[36] !== group.cfg || $[37] !== group.isRootQuestion || $[38] !== hasAutoQ || $[39] !== qForm || $[40] !== subq) {
    t18 = (autoMutation.isPending || autoMutation.isError || hasAutoQ) && <AutoConfigureCard mutation={autoMutation} onReset={() => autoMutation.cancel()} retry={() => {
      autoMutation.mutate(autoReqProps({
        options: {
          forceRefresh: true
        }
      }));
    }}>{hasAutoQ && <div className="space-y-4"><InfoLabel>Suggested configuration based on the title, subtitle and description. Edit the configuration before proposing, and we will update the suggestions taking those changes into account.</InfoLabel>{subq.forecastType && <div className="flex gap-4 items-center"><SubqTypeCombo value={subq.subquestionType} onChange={value => onSubqTypeChange({
            value,
            form: qForm,
            setValuesDescription: "user"
          })} showLabel={true} withinPortal={true} /><ForecastTypeCombo {...qForm.getInputProps("forecastType")} showLabel={true} withinPortal={false} /></div>}<QuestionCommonForm form={qForm as any} simplified={true} />{subq.subquestionType != "related" && <ResolutionsEdit mode="subquestion" className="pt-2" parentQ={{
          ...pick(group.cfg, ["title", "subTitle", "description", "forecastType"]),
          consequenceResolutions: group.isRootQuestion ? group.cfg.resolutions : group.cfg.consequenceResolutions
        }} qForm={qForm} simplified={true} />}<ComparisonSectionControlled uComparison={comparisonProps} mode="new" /><div className="flex items-center justify-end pt-4 pb-3"><Button color="blue" loading={create.isPending} disabled={create.isPending} {...ctrlEnter.buttonProps}>Propose</Button></div></div>}</AutoConfigureCard>;
    $[31] = autoMutation;
    $[32] = autoReqProps;
    $[33] = comparisonProps;
    $[34] = create.isPending;
    $[35] = ctrlEnter.buttonProps;
    $[36] = group.cfg;
    $[37] = group.isRootQuestion;
    $[38] = hasAutoQ;
    $[39] = qForm;
    $[40] = subq;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  return <div className="flex flex-col gap-2 h-full" onKeyDown={t11} ref={titlePopupFocusRef}><div><QuestionTitleForm uTitleForm={titleForm} topRight={<CloseButton onClick={() => {
        onClose({
          clear: true
        });
      }} />} belowForm={t16} /></div>{t17}{t18}</div>;
};
function _temp(obj, pathRaw) {
  const path = (isArray(pathRaw) ? pathRaw.join(".") : pathRaw) as Paths<GroupSubqsForNew>;
  const val = pathValue(obj, path) as string;
  return match(path).with("cfg.description", () => Md.maskNonText(val)).otherwise(() => val);
}
function _temp2(w) {
  return w.replace(/^[^\w]+|[^\w]+$/g, "");
}
function _temp3(w_0) {
  return w_0.toLowerCase();
}
function _temp4(w_1) {
  return w_1.toLowerCase();
}
function _temp5(w_2) {
  return !/^\d+$/.test(w_2);
}
function _temp6(text) {
  return removeStopwords(text.split(/\s+/).map(_temp2).filter(notNullOrEmpty).map(_temp3), [...eng, ...forecastStopwords.map(_temp4)]).filter(_temp5);
}
function _temp7(r) {
  return !isNullOrEmpty(r.matches);
}
function _temp8(r_0) {
  return r_0.item.id;
}
function _temp9(r_1) {
  return r_1.matches;
}
function _temp10(r_2) {
  return r_2.score;
}
function _temp11(_, g) {
  return {
    matches: g.flatMap(_temp9),
    item: g[0].item,
    score: sum(g.map(_temp10).filter(notNull))
  };
}
function _temp12(r_3) {
  return r_3.score ?? 1;
}
function _temp13(t) {
  return <SubqTypeIcon key={t} type={t} variant="icon" />;
}
function _temp14(t0) {
  const {
    id,
    cfg,
    created_at,
    created_by
  } = t0;
  return {
    id,
    cfg: pick(cfg, ["status", "subquestionType", "subquestionOrder", "title", "subTitle", "description", "forecastType"]),
    created_at,
    created_by
  };
}
async function _temp15(p_1, signal) {
  const {
    question: question_0,
    usePrior
  } = await A.getAutoQ(p_1, {
    signal
  }).then(okOrThrow);
  return {
    question: question_0,
    usePrior,
    forRequest: p_1.question
  };
}
function _temp16() {
  return <></>;
}
function _temp17(m) {
  return m.key == "cfg.title";
}
function _temp18(m_0) {
  return m_0.key == "cfg.description";
}